import { Controller } from "@hotwired/stimulus"

// Phase 3.7 Tier 5 — admin: partner walkthrough CRUD + reorder.
//
// Same drag-and-drop + inline-edit pattern as
// integration_admin_categories_controller.js — vanilla HTML5 drag,
// PATCH on save, DELETE with a confirm modal, "Save changes" pulls
// title + category + URL + visibility into one PATCH so admins don't
// have to remember which field needs its own save.
export default class extends Controller {
  static targets = [
    "rows", "row", "titleField", "categoryField", "urlField",
    "createModal", "deleteModal", "deleteLabel"
  ]
  static values = {
    reorderUrl: String,
    createUrl: String,
    updateUrlTemplate: String,
    destroyUrlTemplate: String
  }

  // ── Drag and drop ─────────────────────────────────────
  onDragStart(event) {
    this._draggingId = event.currentTarget.dataset.id
    event.currentTarget.classList.add("opacity-50")
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move"
      event.dataTransfer.setData("text/plain", this._draggingId)
    }
  }

  onDragOver(event) {
    event.preventDefault()
    if (event.dataTransfer) event.dataTransfer.dropEffect = "move"
    const target = event.currentTarget
    if (!this._draggingId || target.dataset.id === this._draggingId) return
    const dragging = this.rowsTarget.querySelector(`[data-id="${this._draggingId}"]`)
    if (!dragging || dragging === target) return
    const rect = target.getBoundingClientRect()
    const before = (event.clientY - rect.top) < (rect.height / 2)
    target.parentNode.insertBefore(dragging, before ? target : target.nextSibling)
  }

  onDrop(event) { event.preventDefault() }

  onDragEnd(event) {
    event.currentTarget.classList.remove("opacity-50")
    if (!this._draggingId) return
    this._draggingId = null
    this._postReorder()
  }

  async _postReorder() {
    const ids = Array.from(this.rowsTarget.querySelectorAll("[data-id]")).map(el => el.dataset.id)
    const body = new FormData()
    ids.forEach(id => body.append("order[]", id))
    try {
      const res = await fetch(this.reorderUrlValue, {
        method: "POST",
        body,
        credentials: "same-origin",
        headers: { "X-CSRF-Token": this._csrfToken(), "Accept": "application/json" }
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.ok) this._toast("Order updated.", "notice")
      else this._toast(data.message || "Couldn't reorder.", "alert")
    } catch (_) {
      this._toast("Network error — try again.", "alert")
    }
  }

  // ── Inline saves ──────────────────────────────────────
  async saveRow(event) {
    const id = event.currentTarget.dataset.id
    const title = this.titleFieldTargets.find(f => f.dataset.id === id)
    const category = this.categoryFieldTargets.find(f => f.dataset.id === id)
    const url = this.urlFieldTargets.find(f => f.dataset.id === id)
    if (!title || !category || !url) return

    const body = new FormData()
    body.append("partner_walkthrough[title]", title.value)
    body.append("partner_walkthrough[category]", category.value)
    body.append("partner_walkthrough[video_embed_url]", url.value)
    await this._patch(this.updateUrlTemplateValue.replace("ID", id), body, "Saved.")
  }

  async toggleVisible(event) {
    const btn = event.currentTarget
    const next = btn.dataset.state !== "true"
    const body = new FormData()
    body.append("partner_walkthrough[visible]", next ? "true" : "false")
    const ok = await this._patch(
      this.updateUrlTemplateValue.replace("ID", btn.dataset.id),
      body,
      next ? "Now visible." : "Now hidden."
    )
    if (ok) {
      btn.dataset.state = next ? "true" : "false"
      btn.textContent = next ? "Visible" : "Hidden"
      btn.className = (next
        ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
        : "bg-gray-100 text-gray-600 hover:bg-gray-200") + " inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[12px] font-medium cursor-pointer"
    }
  }

  // ── Create modal ──────────────────────────────────────
  openCreate() { this.createModalTarget.classList.remove("hidden") }
  closeCreate() { this.createModalTarget.classList.add("hidden") }

  async submitCreate(event) {
    event.preventDefault()
    const body = new FormData(event.currentTarget)
    try {
      const res = await fetch(this.createUrlValue, {
        method: "POST",
        body,
        credentials: "same-origin",
        headers: { "X-CSRF-Token": this._csrfToken(), "Accept": "application/json" }
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.ok) this._flashAndReload(data.message || "Walkthrough added.", "notice")
      else this._toast(data.message || "Couldn't create.", "alert")
    } catch (_) {
      this._toast("Network error — try again.", "alert")
    }
  }

  // ── Delete confirm ────────────────────────────────────
  confirmDelete(event) {
    this._deleteId = event.currentTarget.dataset.id
    if (this.hasDeleteLabelTarget) {
      this.deleteLabelTarget.textContent = event.currentTarget.dataset.name || "this walkthrough"
    }
    this.deleteModalTarget.classList.remove("hidden")
  }

  closeDelete() {
    this.deleteModalTarget.classList.add("hidden")
    this._deleteId = null
  }

  async submitDelete() {
    if (!this._deleteId) return
    try {
      const res = await fetch(this.destroyUrlTemplateValue.replace("ID", this._deleteId), {
        method: "DELETE",
        credentials: "same-origin",
        headers: { "X-CSRF-Token": this._csrfToken(), "Accept": "application/json" }
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.ok) this._flashAndReload(data.message || "Removed.", "notice")
      else this._toast(data.message || "Couldn't remove.", "alert")
    } catch (_) {
      this._toast("Network error — try again.", "alert")
    } finally {
      this.closeDelete()
    }
  }

  stopPropagation(event) { event.stopPropagation() }

  // ── Helpers ───────────────────────────────────────────
  async _patch(url, body, successMessage) {
    try {
      const res = await fetch(url, {
        method: "PATCH",
        body,
        credentials: "same-origin",
        headers: { "X-CSRF-Token": this._csrfToken(), "Accept": "application/json" }
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.ok) {
        this._toast(successMessage || data.message || "Saved.", "notice")
        return true
      }
      this._toast(data.message || "Couldn't save.", "alert")
      return false
    } catch (_) {
      this._toast("Network error — try again.", "alert")
      return false
    }
  }

  _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  _toast(message, type) {
    const div = document.createElement("div")
    div.className = type === "notice" ? "flash-toast-notice" : "flash-toast-alert"
    div.dataset.controller = "flash"
    div.dataset.flashDismissAfterValue = "5000"
    div.textContent = message
    document.body.prepend(div)
  }

  _flashAndReload(message, type) {
    try {
      sessionStorage.setItem("integration-admin.flash",
        JSON.stringify({ message, type, ts: Date.now() }))
    } catch (_) {}
    window.location.reload()
  }
}
